import Linkbar from "@coworker/apprestructured/src/shared/components/Linkbar/Linkbar";
import { ReactElement, useCallback, useMemo, useState } from "react";
import { CreateTaskTypeDTO } from "../../../types/TaskType";
import styles from "./TaskTypesCreatePage.module.css";
import { useCreateTaskType } from "../../../services/useTaskTypes";
import { HttpError } from "@coworker/apprestructured/src/shared/types/exception";
import { FixaToast } from "@coworker/apprestructured/src/shared/wrappers/FixaToast/FixaToast";
import TaskTypeForm, {
  ErrorMessageAndException,
} from "../../../components/TaskTypeForm/TaskTypeForm";
import ViewerTopFragmentConfig from "../../../components/TaskViewerSheet/ViewerTop/viewer-top-fragment/ViewerTopFragmentConfig";
import { TitleCreatorConfig } from "../../../components/TaskCreatorAndEditorSheet/TitleCreator/TitleCreatorConfig";

export default function TaskTypesCreatePage() {
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<ReactElement | null>(null);

  const { mutateAsync: mutateCreateTaskType } = useCreateTaskType();

  const showErrors = (errors: ErrorMessageAndException[]) => {
    const errorTexts: string[] = errors.map(({ message: text, exception }) => {
      const exceptionText =
        exception instanceof Error
          ? exception.toString()
          : exception instanceof HttpError
            ? `${exception.statusText}(${exception.status})`
            : "unknown exception";
      return `${text}, ${exceptionText}`;
    });

    console.warn(errorTexts.join("\n"));
    setError(
      <>
        {errorTexts.map((text, idx) => (
          <p key={`${idx}-${text}`}>{text}</p>
        ))}
      </>
    );
  };

  const onSave = useCallback(
    async (errors: ErrorMessageAndException[], taskType: CreateTaskTypeDTO) => {
      const allErrors = [...errors];

      if (!allErrors.length) {
        try {
          await mutateCreateTaskType(taskType);
        } catch (exception) {
          allErrors.push({
            message: "Failed to save taskType to server",
            exception,
          });
        }
      }

      if (allErrors.length) {
        showErrors(allErrors);
        return false;
      } else {
        console.info("TaskType created!");
        return true;
      }
    },
    [mutateCreateTaskType]
  );

  const initialTaskTypeValues: CreateTaskTypeDTO = useMemo(
    () => ({
      name: "",
      label: "",
      title_creator: {} as unknown as TitleCreatorConfig,
      creator_fragments: [],
      viewer_top_fragment: {} as unknown as ViewerTopFragmentConfig,
      viewer_fragments: [],
      close_reason_ids: [],
      card_fragments: [],
      details: [],
    }),
    []
  );

  return (
    <>
      <Linkbar
        items={[
          {
            label: "Task types",
            to: "/task-types",
          },
        ]}
        currentLabel="Create"
        currentAsH1
      />

      <div className={styles["task-types-create-page"]}>
        <TaskTypeForm<CreateTaskTypeDTO>
          readOnly={false}
          onSave={onSave}
          taskTypeValues={initialTaskTypeValues}
          buttonText="Create task type"
        />
      </div>

      <FixaToast
        className={styles["task-types-error-toast"]}
        text={error ?? ""}
        isOpen={!!error}
        onCloseRequest={() => setError(null)}
      />

      <FixaToast
        className={styles["task-types-success-toast"]}
        text={"TaskType created!"}
        isOpen={!!success}
        onCloseRequest={() => {
          setSuccess(false);
        }}
      />
    </>
  );
}
